import { useMemo, useState } from 'react';
import { Box, Link, List, ListItem, ListItemText, Theme, Typography } from '@mui/material';
import { Dialog } from '@components/dialog';
import { Table } from '@components/table/Table';
import { TableGridBaseColDef } from '@components/table/types/TableColumnDef.type';
import { useStore } from '@store/GlobalStore';
import { shallow } from 'zustand/shallow';
import { getDateFormatter, LongDateFormatWithoutSeconds } from '@utils/date';
import { format } from 'date-fns';
import { LazyJsonViewer } from '@components/json/LazyJsonViewer';
import { useSearchQuoteDeclarations } from '@services/quotes/quoteDeclaration';
import { useGetFraudStatement } from '@services/fraud-statement';
import type { QuoteDeclarationSearchRequestQuery, QuoteDeclarationSearchResponse } from '@lio/api-types';
import { adjustFontSize } from '@theme/styleOverrides/typography';

export type QuoteDeclarationsModalProps = {
  open: boolean;
  onClose: () => void;
  quoteId: string;
};

type DeclarationRow = QuoteDeclarationSearchResponse['items'][number];

type TQuoteDeclarationsTable = {
  items: DeclarationRow[];
  params: QuoteDeclarationSearchRequestQuery;
};

const declarationTypeLabels: Record<string, string> = {
  FormsAndExclusions: 'Forms & Exclusions',
  FraudStatement: 'Fraud Statement',
  IncompleteProposalNotice: 'Incomplete Proposal Notice',
};

const getDeclarationTypeLabel = (type: string): string => declarationTypeLabels[type] ?? type;

const isStatementsDeclaration = (
  declaration: unknown,
): declaration is { statements: string[]; [key: string]: unknown } => {
  if (typeof declaration !== 'object' || declaration === null) return false;
  const obj = declaration as Record<string, unknown>;
  return Array.isArray(obj.statements);
};

const formatPropertyLabel = (key: string): string => {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
};

export const QuoteDeclarationsModal = ({ open, onClose, quoteId }: QuoteDeclarationsModalProps) => {
  const [layoutPreferences] = useStore((state) => [state.layoutPreferences], shallow);
  const clockFormat = layoutPreferences.clockFormat;

  const [selectedDeclaration, setSelectedDeclaration] = useState<DeclarationRow | null>(null);

  const isFraudStatement = selectedDeclaration?.declarationType === 'FraudStatement';
  const { fraudStatement } = useGetFraudStatement(isFraudStatement ? selectedDeclaration?.fraudStatementId : undefined);

  const columns: TableGridBaseColDef<DeclarationRow>[] = useMemo(() => {
    return [
      {
        field: 'createdAt',
        headerName: 'Date',
        minWidth: 180,
        sortable: false,
        valueGetter: (_, row) => {
          if (!row.createdAt) return null;
          return format(new Date(row.createdAt), getDateFormatter(clockFormat, LongDateFormatWithoutSeconds));
        },
      },
      {
        field: 'declarationType',
        headerName: 'Declaration Type',
        minWidth: 200,
        sortable: false,
        valueGetter: (_, row) => getDeclarationTypeLabel(row.declarationType),
      },
      {
        field: 'declaredBy',
        headerName: 'Declared By',
        flex: 1,
        minWidth: 220,
        sortable: false,
        valueGetter: (_, row) => {
          const user = row.declaredByUser;
          return `${user.firstName ?? ''} ${user.lastName ?? ''} (${user.email ?? '-'})`;
        },
      },
      {
        field: 'viewDeclaration',
        headerName: '',
        minWidth: 160,
        sortable: false,
        renderCell: ({ row }) => (
          <Link
            component='button'
            underline='hover'
            sx={{ color: 'secondary.main', fontWeight: 500 }}
            onClick={() => setSelectedDeclaration(row)}
          >
            View Declaration
          </Link>
        ),
      },
    ];
  }, [clockFormat]);

  const useSearchWrapper = (params: Partial<QuoteDeclarationSearchRequestQuery>) => {
    const searchParams = {
      ...params,
      sort: params.sort ?? '-createdAt',
    } as QuoteDeclarationSearchRequestQuery;

    return useSearchQuoteDeclarations(quoteId, searchParams);
  };

  const renderDeclarationContent = (declaration: unknown) => {
    if (isStatementsDeclaration(declaration)) {
      const { statements, ...rest } = declaration;
      return (
        <Box>
          <Typography variant='body2' sx={{ mb: 1 }}>
            Statements
          </Typography>
          <List dense disablePadding>
            {statements.map((statement, index) => (
              <ListItem key={index} sx={{ pl: 2 }}>
                <ListItemText
                  primary={statement}
                  primaryTypographyProps={{ variant: 'body2', sx: { fontWeight: 400 } }}
                />
              </ListItem>
            ))}
          </List>
          {Object.keys(rest).length > 0 && (
            <Box sx={{ mt: 2 }}>
              {Object.entries(rest).map(([key, value]) => (
                <Box key={key} sx={{ mb: 1 }}>
                  <Typography variant='body2'>
                    {formatPropertyLabel(key)}:{' '}
                    <Box component='span' sx={{ fontWeight: 400 }}>
                      {typeof value === 'string' ? value : JSON.stringify(value)}
                    </Box>
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      );
    }

    return (
      <LazyJsonViewer
        value={declaration}
        defaultInspectDepth={100}
        objectSortKeys
        sx={{
          overflowY: 'auto',
          fontSize: ({ palette }: Theme) => `${adjustFontSize(11, palette.fontSize)}px`,
          fontWeight: 300,
          backgroundColor: 'white',
        }}
      />
    );
  };

  return (
    <Dialog
      open={open}
      title='Quote Declarations'
      secondaryBtnText='Exit'
      secondaryBtnAction={onClose}
      handleCloseDialog={onClose}
      primaryBtnText={undefined}
      size='extraLarge'
      fullWidth
    >
      <Box sx={{ padding: '16px 24px', minHeight: '400px' }}>
        <Table<TQuoteDeclarationsTable>
          ariaLabel='quote-declarations-table'
          onLoadData={useSearchWrapper}
          columns={columns}
          paginationConfig={{ currentPage: 0, rowsPerPage: 10, pageOffset: 0 }}
          noResultsConfig={{
            disableNoMatchFound: false,
            noResultsMessage: 'No declarations have been made for this quote.',
          }}
        />
      </Box>

      {selectedDeclaration && (
        <Dialog
          open={!!selectedDeclaration}
          title='Declaration Details'
          secondaryBtnText='Exit'
          secondaryBtnAction={() => setSelectedDeclaration(null)}
          handleCloseDialog={() => setSelectedDeclaration(null)}
          primaryBtnText={undefined}
          size='large'
          fullWidth
        >
          <Box sx={{ padding: '16px 24px' }}>
            <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant='body2'>
                Declared By:{' '}
                <Box component='span' sx={{ fontWeight: 400 }}>
                  {`${selectedDeclaration.declaredByUser.firstName} ${selectedDeclaration.declaredByUser.lastName}`}
                </Box>
              </Typography>
              <Typography variant='body2'>
                Date:{' '}
                <Box component='span' sx={{ fontWeight: 400 }}>
                  {selectedDeclaration.createdAt
                    ? format(
                        new Date(selectedDeclaration.createdAt),
                        getDateFormatter(clockFormat, LongDateFormatWithoutSeconds),
                      )
                    : '-'}
                </Box>
              </Typography>
              <Typography variant='body2'>
                Type:{' '}
                <Box component='span' sx={{ fontWeight: 400 }}>
                  {getDeclarationTypeLabel(selectedDeclaration.declarationType)}
                </Box>
              </Typography>
            </Box>
            {isFraudStatement && fraudStatement && (
              <Box sx={{ mt: 2, mb: 2 }}>
                <Typography variant='body2' sx={{ mb: 1 }}>
                  Fraud Statement ({fraudStatement.jurisdiction.name})
                </Typography>
                <Typography variant='body2' sx={{ fontWeight: 400, whiteSpace: 'pre-wrap' }}>
                  {fraudStatement.statement}
                </Typography>
              </Box>
            )}
            <Box sx={{ mt: 2 }}>{renderDeclarationContent(selectedDeclaration.declaration)}</Box>
          </Box>
        </Dialog>
      )}
    </Dialog>
  );
};
